import type { MTMetadataFormField } from '@/multi-territory/mt-types/mt-metadata'
import { KeyboardArrowDown } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

const FIELD_HEIGHT = '48px'

const LABEL_SX = {
  fontSize: 'var(--font-size-md, 16px)',
  fontWeight: 500,
  color: 'var(--forbrand-primary, #111113)',
  mb: 1,
  lineHeight: '140%',
}

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  height: FIELD_HEIGHT,
  minHeight: FIELD_HEIGHT,
  padding: '12px 12px 12px 16px',
  fontSize: '14px',
  border: '1px solid var(--border-strong, #8E95A2)',
  borderRadius: '0',
  background: 'var(--bg-white, #FFFFFF)',
  outline: 'none',
  boxSizing: 'border-box',
  color: 'var(--forbrand-primary, #111113)',
}

const INPUT_STYLE_ERROR: React.CSSProperties = {
  ...INPUT_STYLE,
  border: '1px solid var(--forbrand-error, #d12121)',
}

const INPUT_STYLE_DISABLED: React.CSSProperties = {
  ...INPUT_STYLE,
  background: 'var(--bg-disabled, #dfe3e6)',
  cursor: 'not-allowed',
  color: 'var(--forbrand-secondary, #626771)',
}

const getSelectSx = (hasError: boolean, disabled: boolean) => ({
  width: '100%',
  maxWidth: '100%',
  height: FIELD_HEIGHT,
  minHeight: FIELD_HEIGHT,
  borderRadius: '0',
  fontSize: 'var(--font-size-sm, 14px)',
  backgroundColor: disabled ? 'var(--bg-disabled, #dfe3e6)' : 'var(--bg-white, #FFFFFF)',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? 'var(--forbrand-error, #d12121)' : 'var(--border-strong, #8E95A2)',
    borderWidth: '1px',
    borderRadius: '0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? 'var(--forbrand-error, #d12121)' : 'var(--forbrand-secondary, #626771)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? 'var(--forbrand-error, #d12121)' : 'var(--forbrand-color, #fd5108)',
    borderWidth: '1px',
  },
  '& .MuiSelect-select': {
    padding: '12px 32px 12px 16px !important',
    fontSize: 'var(--font-size-sm, 14px)',
    color: 'var(--forbrand-primary, #111113)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  '& .MuiSelect-icon': {
    color: 'var(--forbrand-secondary, #626771)',
    fontSize: '20px',
    right: '12px',
  },
})

const MENU_PROPS = {
  PaperProps: {
    sx: {
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      border: '1px solid var(--border-strong, #8E95A2)',
      borderRadius: '4px',
      mt: 0.5,
      maxHeight: '300px',
      width: '225px',
    },
  },
  MenuListProps: {
    sx: {
      '& .MuiMenuItem-root': {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
  },
}

const HELPER_SX = {
  color: 'var(--forbrand-error, #d12121)',
  mt: 0.5,
  fontSize: 'var(--font-size-xs, 12px)',
}

const FIELD_WRAPPER_SX = {
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
}

// --- Props ------------------------------------------------------------------

interface MTFormFieldProps {
  field: MTMetadataFormField
  value: string
  error?: string
  onChange: (key: string, value: string) => void
  onMultiChange?: (key: string, values: string[]) => void
  minDate?: string
  /** Override disabled — when true disables regardless of field.editable */
  disabled?: boolean
}

export const MT_MULTI_VALUE_DELIMITER = '||'

// --- Main Component ---------------------------------------------------------

export function MTFormField({ field, value, error, onChange, onMultiChange, minDate, disabled }: MTFormFieldProps) {
  const hasError = !!error
  const isDisabled = disabled !== undefined ? disabled : !field.editable

  switch (field.controlType) {
    case 'dropdown':
      return <MTDropdownField field={field} value={value} error={error} hasError={hasError} onChange={onChange} disabled={isDisabled} />
    case 'multiselect':
      return <MTMultiSelectField field={field} value={value} error={error} hasError={hasError} onMultiChange={onMultiChange} disabled={isDisabled} />
    case 'date':
      return <MTDateField field={field} value={value} error={error} hasError={hasError} onChange={onChange} minDate={minDate} disabled={isDisabled} />
    case 'number':
      return <MTNumberField field={field} value={value} error={error} hasError={hasError} onChange={onChange} disabled={isDisabled} />
    default:
      return <MTTextField field={field} value={value} error={error} hasError={hasError} onChange={onChange} disabled={isDisabled} />
  }
}

// --- Dropdown ---------------------------------------------------------------

function MTDropdownField({ field, value, error, hasError, onChange, disabled }: {
  field: MTMetadataFormField; value: string; error?: string; hasError: boolean
  onChange: (key: string, value: string) => void; disabled: boolean
}) {
  const options = field.options || []
  return (
    <Box sx={FIELD_WRAPPER_SX}>
      <Typography sx={LABEL_SX}>
        {field.label}
        {field.required && <span style={{ color: 'var(--forbrand-error, #d12121)' }}>*</span>}
      </Typography>
      <FormControl fullWidth size="small" error={hasError} disabled={disabled}>
        <Select
          value={value || ''}
          onChange={e => onChange(field.key, e.target.value as string)}
          IconComponent={KeyboardArrowDown}
          displayEmpty
          renderValue={selected => {
            if (!selected) return <span style={{ color: 'var(--text-placeholder, #999)' }}>{'Select ' + field.label.toLowerCase()}</span>
            const opt = options.find(o => o.value === selected)
            return <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt ? opt.label : selected}</span>
          }}
          sx={getSelectSx(hasError, disabled)}
          MenuProps={MENU_PROPS}
        >
          <MenuItem value="" disabled sx={{ py: 0.75, px: 2, minHeight: '36px', fontSize: 'var(--font-size-sm, 14px)' }}>
            <span style={{ color: 'var(--text-placeholder, #999)' }}>{'Select ' + field.label.toLowerCase()}</span>
          </MenuItem>
          {options.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{
              fontSize: 'var(--font-size-sm, 14px)', color: 'var(--forbrand-primary, #111113)',
              py: 0.75, px: 2, minHeight: '36px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4,
              '&:hover': { backgroundColor: 'var(--bg-layer-1, #f5f7f8)' },
              '&.Mui-selected': { backgroundColor: 'var(--table-header-bg, #eeeff1)', '&:hover': { backgroundColor: '#e5e7eb' } },
            }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        {error && <Typography variant="caption" sx={HELPER_SX}>{error}</Typography>}
      </FormControl>
    </Box>
  )
}

// --- Multi Select -----------------------------------------------------------

function MTMultiSelectField({ field, value, error, hasError, onMultiChange, disabled }: {
  field: MTMetadataFormField; value: string; error?: string; hasError: boolean
  onMultiChange?: (key: string, values: string[]) => void; disabled: boolean
}) {
  const options = field.options || []
  const selectedValues = value ? value.split(MT_MULTI_VALUE_DELIMITER).filter(Boolean) : []
  const allValues = options.map(o => o.value)
  const allSelected = selectedValues.length === allValues.length && allValues.length > 0

  const handleChange = (e: { target: { value: unknown } }) => {
    const selected = e.target.value as string[]
    if (selected.includes('__ALL__')) {
      onMultiChange?.(field.key, allSelected ? [] : allValues)
    } else {
      onMultiChange?.(field.key, selected)
    }
  }

  return (
    <Box sx={FIELD_WRAPPER_SX}>
      <Typography sx={LABEL_SX}>
        {field.label}
        {field.required && <span style={{ color: 'var(--forbrand-error, #d12121)' }}>*</span>}
      </Typography>
      <FormControl fullWidth size="small" error={hasError} disabled={disabled}>
        <Select
          multiple
          value={selectedValues}
          onChange={handleChange}
          IconComponent={KeyboardArrowDown}
          displayEmpty
          renderValue={selected => {
            const sel = selected as string[]
            if (!sel || sel.length === 0) return <span style={{ color: 'var(--text-placeholder, #999)' }}>{'Select ' + field.label.toLowerCase()}</span>
            const text = options.filter(o => sel.includes(o.value)).map(o => o.label).join(', ')
            return <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
          }}
          sx={getSelectSx(hasError, disabled)}
          MenuProps={MENU_PROPS}
        >
          <MenuItem value="__ALL__" sx={{ fontSize: 'var(--font-size-sm, 14px)', py: 1 }}>
            <Checkbox checked={allSelected} indeterminate={selectedValues.length > 0 && selectedValues.length < allValues.length} size="small"
              sx={{ color: 'var(--forbrand-color, #fd5108)', '&.Mui-checked': { color: 'var(--forbrand-color, #fd5108)' }, '&.MuiCheckbox-indeterminate': { color: 'var(--forbrand-color, #fd5108)' } }} />
            <ListItemText primary="Select All" primaryTypographyProps={{ fontSize: 'var(--font-size-sm, 14px)' }} />
          </MenuItem>
          {options.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 'var(--font-size-sm, 14px)', py: 1, whiteSpace: 'normal', wordBreak: 'break-word' }}>
              <Checkbox checked={selectedValues.includes(opt.value)} size="small"
                sx={{ color: 'var(--forbrand-color, #fd5108)', '&.Mui-checked': { color: 'var(--forbrand-color, #fd5108)' } }} />
              <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: 'var(--font-size-sm, 14px)', lineHeight: 1.5 }} />
            </MenuItem>
          ))}
        </Select>
        {error && <Typography variant="caption" sx={HELPER_SX}>{error}</Typography>}
      </FormControl>
    </Box>
  )
}

// --- Date -------------------------------------------------------------------

function MTDateField({ field, value, error, hasError, onChange, minDate, disabled }: {
  field: MTMetadataFormField; value: string; error?: string; hasError: boolean
  onChange: (key: string, value: string) => void; minDate?: string; disabled: boolean
}) {
  const minDateValue = minDate ? dayjs(minDate, 'DD/MM/YYYY') : undefined
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={FIELD_WRAPPER_SX}>
        <Typography sx={LABEL_SX}>
          {field.label}
          {field.required && <span style={{ color: 'var(--forbrand-error, #d12121)' }}>*</span>}
        </Typography>
        <DatePicker
          value={value ? dayjs(value, 'DD/MM/YYYY') : null}
          onChange={(date: Dayjs | null) => onChange(field.key, date ? date.format('DD/MM/YYYY') : '')}
          format="DD/MM/YYYY"
          disabled={disabled}
          minDate={minDateValue?.isValid() ? minDateValue : undefined}
          shouldDisableDate={(day: Dayjs) => minDateValue?.isValid() ? day.isBefore(minDateValue, 'day') : false}
          slotProps={{
            textField: {
              fullWidth: true,
              placeholder: 'DD/MM/YYYY',
              error: hasError,
              sx: {
                width: '100%',
                maxWidth: '100%',
                '& .MuiInputBase-root': { height: '48px', minHeight: '48px' },
                '& .MuiOutlinedInput-root': {
                  width: '100%', height: '48px', minHeight: '48px', borderRadius: '0',
                  fontSize: 'var(--font-size-sm, 14px)',
                  backgroundColor: disabled ? 'var(--bg-disabled, #dfe3e6)' : 'var(--bg-white, #FFFFFF)',
                  '& .MuiOutlinedInput-input': { padding: '12px 12px 12px 16px', fontSize: 'var(--font-size-sm, 14px)', height: 'auto' },
                  '& fieldset': { borderColor: hasError ? 'var(--forbrand-error, #d12121)' : 'var(--border-strong, #8E95A2)', borderWidth: '1px', borderRadius: '0' },
                  '&:hover fieldset': { borderColor: hasError ? 'var(--forbrand-error, #d12121)' : 'var(--forbrand-secondary, #626771)' },
                  '&.Mui-focused fieldset': { borderColor: hasError ? 'var(--forbrand-error, #d12121)' : 'var(--forbrand-color, #fd5108)', borderWidth: '1px' },
                },
              },
            },
          }}
        />
        {error && (
          <Typography variant="caption" sx={HELPER_SX}>
            {error}
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  )
}

// --- Number -----------------------------------------------------------------

function MTNumberField({ field, value, error, hasError, onChange, disabled }: {
  field: MTMetadataFormField; value: string; error?: string; hasError: boolean
  onChange: (key: string, value: string) => void; disabled: boolean
}) {
  const style = disabled ? INPUT_STYLE_DISABLED : hasError ? INPUT_STYLE_ERROR : INPUT_STYLE
  return (
    <Box sx={FIELD_WRAPPER_SX}>
      <Typography sx={LABEL_SX}>
        {field.label}
        {field.required && <span style={{ color: 'var(--forbrand-error, #d12121)' }}>*</span>}
      </Typography>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        placeholder={'Enter ' + field.label.toLowerCase()}
        disabled={disabled}
        onChange={e => {
          const val = e.target.value
          if (val === '') { onChange(field.key, val); return }
          if (!/^[0-9]*\.?[0-9]*$/.test(val)) return
          const decimalMatch = val.match(/\.(.*)/)
          const decimalPlaces = decimalMatch ? decimalMatch[1].length : 0
          const intPart = val.split('.')[0] || ''
          const maxDecimals = field.validations?.regex?.includes('{1,2}') ? 2 : undefined
          const maxIntDigits = field.validations?.regex?.includes('{1,3}') ? 3 : (field.validations?.max !== undefined ? String(Math.floor(field.validations.max)).length : undefined)
          if (maxDecimals !== undefined && decimalPlaces > maxDecimals) return
          if (maxIntDigits !== undefined && intPart.length > maxIntDigits) return
          onChange(field.key, val)
        }}
        style={style}
      />
      {error && <Typography variant="caption" sx={HELPER_SX}>{error}</Typography>}
    </Box>
  )
}

// --- Text -------------------------------------------------------------------

function MTTextField({ field, value, error, hasError, onChange, disabled }: {
  field: MTMetadataFormField; value: string; error?: string; hasError: boolean
  onChange: (key: string, value: string) => void; disabled: boolean
}) {
  const style = disabled ? INPUT_STYLE_DISABLED : hasError ? INPUT_STYLE_ERROR : INPUT_STYLE
  return (
    <Box sx={FIELD_WRAPPER_SX}>
      <Typography sx={LABEL_SX}>
        {field.label}
        {field.required && <span style={{ color: 'var(--forbrand-error, #d12121)' }}>*</span>}
      </Typography>
      <input
        type="text"
        value={value}
        placeholder={'Enter ' + field.label.toLowerCase()}
        disabled={disabled}
        onChange={e => onChange(field.key, e.target.value)}
        style={style}
      />
      {error && <Typography variant="caption" sx={HELPER_SX}>{error}</Typography>}
    </Box>
  )
}

export default MTFormField
